import React, { useState, useEffect } from "react";
import { ArrowLeft, Users, Search, Mail, Phone, Briefcase } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getTeamMembers } from "../../services/hrms.service";

const TeamMembers = () => {
  const navigate = useNavigate();

  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  

  const [searchTerm, setSearchTerm] = useState("");

  // Fetch team members on component mount
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getTeamMembers();

        if (response.status === 1) {
          // Transform API data to match component structure
          const transformedMembers = response.data.map((member, index) => ({
            id: member.id,
            name: `${member.first_name} ${member.last_name}`,
            empId: member.employee_id || member.id.toString(),
            email: member.email || "",
            phone: member.phone || "",
            designation: member.designation || "Employee",
            department: member.department || "Not Assigned",
            status: "Present", // Default status, could be enhanced with attendance data
            statusColor: "text-green-600",
            joiningDate: member.joining_date ? new Date(member.joining_date).toLocaleDateString('en-IN') : "N/A",
            workingHours: 160, // Default, could be calculated from attendance
            overtime: 0, // Default
          }));
          setTeamMembers(transformedMembers);
        } else {
          setError("Failed to fetch team members");
        }
      } catch (err) {
        console.error("Error fetching team members:", err);
        setError("Failed to load team members. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, []);

  const filteredMembers = teamMembers.filter(
    (member) =>
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.empId.includes(searchTerm)
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-12xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/manager/dashboard")}
            className="flex items-center text-indigo-600 hover:text-indigo-700 mb-4 font-medium"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Team Members
              </h1>
          <p className="text-gray-600">
            Manage and view your team of {filteredMembers.length} members
          </p>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or employee ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading team members...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 rounded-lg shadow-sm border border-red-200 p-6 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg"
            >
              Retry
            </button>
          </div>
        )}

        {/* Team Members Grid */}
        {!loading && !error && filteredMembers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMembers.map((member) => (
              <div
                key={member.id}
                className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition"
              >
                {/* Profile Header */}
                <div className="flex items-center space-x-4 mb-4 pb-4 border-b">
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                    {member.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">
                      {member.name}
                    </h3>
                    <p className="text-sm text-gray-500">{member.designation}</p>
                    <span
                      className={`inline-block mt-1 text-xs font-medium ${member.statusColor}`}
                    >
                      ● {member.status}
                    </span>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Briefcase className="w-4 h-4 mr-2 text-gray-400" />
                    <span>ID: {member.empId}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="truncate">{member.email}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                    <span>{member.phone}</span>
                  </div>
                </div>

                {/* Working Hours */}
                <div className="bg-purple-50 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-purple-900">
                      Working Hours (This Month)
                    </span>
                    <span className="text-sm font-bold text-purple-700">
                      {member.workingHours}h
                    </span>
                  </div>
                  {member.overtime > 0 && (
                    <div className="text-xs text-purple-600">
                      +{member.overtime}h overtime
                    </div>
                  )}
                </div>

                {/* Action Button */}
                <button
                  onClick={() => navigate(`/manager/employee/${member.id}`)}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition"
                >
                  View Full Profile
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredMembers.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              No team members found matching your search
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamMembers;